import clsx from "clsx";
import { LabelHTMLAttributes, PropsWithChildren, useState } from "react";
import {
  CalculatorAnswerTypes,
  CalculatorSession,
} from "../../../../types/Co2CalculatorAnswerTypes";
import { ArrayElement } from "../../../../types/util";
import { calculatorActions } from "../../../redux/co2calculatorSlice";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import {
  getValueFromAnswerValue,
  hasAnswerDataOptions,
  usePrismaTranslations,
} from "../../../util/calculator";
import { TSelectCurrentQuestion } from "../../../util/prismaHelper";
import Input from "../../Input/Input";
import BasicRadioInput from "../../Input/Radio/BasicRadioInput";
import styles from "./RadioAnswer.module.scss";

export type SelectAnswerProps = {
  answer: ArrayElement<TSelectCurrentQuestion["QAnswer"]>;
  qId: string;
};

function RadioAnswer({ answer, qId }: SelectAnswerProps) {
  const tPrisma = usePrismaTranslations(answer.QAnswerLabelTranslation);
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useAppDispatch();
  const state = useAppSelector((state) => state.calculator);
  const [customInputValue, setCustomInputValue] = useState<
    string | number | undefined
  >(
    state.answers[qId]
      ? (state.answers[qId].value as Record<string, string | number>)[
          answer.id
        ] === "custom"
        ? (state.answers[qId].value as Record<string, string | number>)[
            answer.id + "_custom"
          ]
        : ""
      : ""
  );

  if (!answer.data) return null;

  const onChange = async (
    value: string | number,
    d: CalculatorAnswerTypes.BaseRadioData,
    optOrReplace?: CalculatorAnswerTypes.RadioOption | boolean,
    replaceAnswer?: boolean
  ) => {
    process.env.NODE_ENV === "development" && console.time("OnChange");
    setIsLoading(true);
    const promises = [];
    const replace =
      typeof optOrReplace === "boolean" ? optOrReplace : replaceAnswer;
    console.log("onChange", value, d, optOrReplace, replaceAnswer);
    if (typeof optOrReplace === "object") {
      let u: string | number = 1;
      // FIXME: use `._usesQuestionIdAndAnswerId`-Array for calculation
      if (
        optOrReplace._usesQuestionIdAndAnswerId != null &&
        optOrReplace._usesQuestionIdAndAnswerId.length > 0
      ) {
        console.log(
          "Using new `_usesQuestionIdAndAnswerId` field",
          optOrReplace._usesQuestionIdAndAnswerId
        );
        u = optOrReplace._usesQuestionIdAndAnswerId.reduce((prev, curr) => {
          const v = state.answers[curr.questionId];
          if (curr.answerId == null) {
            return (
              prev *
              ((typeof v.value === "string"
                ? parseInt(v.value)
                : v.value) as number)
            );
          } else {
            const value = (v.value as Record<string, string | number>)[
              curr.answerId
            ];
            return prev * (typeof value === "string" ? parseInt(value) : value);
          }
        }, 1);
      } else {
        if (optOrReplace._usesQuestionId != null) {
          if (optOrReplace._usesAnswerId != null) {
            u = (
              state.answers[optOrReplace._usesQuestionId].value as Record<
                string,
                string | number
              >
            )[optOrReplace._usesAnswerId];
          } else {
            u = state.answers[optOrReplace._usesQuestionId].value as
              | string
              | number;
          }
        }
      }
      if (typeof u === "string") {
        u = parseInt(u);
      }
      if (isNaN(u)) {
        u = 1;
      }
      let v = optOrReplace.value ?? 1;
      if (optOrReplace.type === "input" && !optOrReplace.value) {
        v =
          typeof customInputValue === "string"
            ? parseInt(customInputValue)
            : customInputValue ?? 0;
        if (isNaN(v)) {
          v = 0;
        }
      }

      /*
      Weil die "Nein" answer nicht standalone ist, wird sie nicht ersetzt, sondern nur geupdated.
      Dadurch denkt die berechnung, dass die "0" value immer noch gültig ist, wenn wir es deselecten
       */

      promises.push(
        dispatch(
          calculatorActions.updateData({
            questionId: qId,
            value: u * v * (optOrReplace.factor ?? 1),
            data: optOrReplace,
            multi: answer.standalone ? answer.id : undefined,
            category: state.currentQuestion!.category,
          })
        )
      );
    } else {
      if (!d._ignore) {
        if (
          (d._usesQuestionIdAndAnswerId != null &&
            d._usesQuestionIdAndAnswerId.length > 0) ||
          (d._usesAnswerId && d._usesQuestionId)
        ) {
          promises.push(
            dispatch(
              calculatorActions.updateData({
                questionId: qId,
                factor: d.factor ?? 1,
                _usesAnswerId: d._usesAnswerId,
                _usesQuestionId: d._usesQuestionId,
                _usesQuestionIdAndAnswerId: d._usesQuestionIdAndAnswerId,
                data: d,
                multi: answer.standalone ? answer.id : undefined,
                category: state.currentQuestion!.category,
              })
            )
          );
        } else {
          let v = d.value ?? 1;
          if (d.type === "input" && !d.value) {
            v =
              typeof customInputValue === "string"
                ? parseInt(customInputValue)
                : customInputValue ?? 0;
            if (isNaN(v)) {
              v = 0;
            }
          }
          promises.push(
            dispatch(
              calculatorActions.updateData({
                questionId: qId,
                value: (d.factor ?? 1) * v,
                data: d,
                multi: answer.standalone ? answer.id : undefined,
                category: state.currentQuestion!.category,
              })
            )
          );
        }
      }
    }
    const value1: CalculatorSession.Answers["string"] = {
      value: { [answer.id]: value },
      _meta: {
        [answer.id]: { ...d, category: state.currentQuestion!.category },
      },
    };
    if (
      d.type === "input" ||
      (typeof optOrReplace === "object" && optOrReplace?.type === "input")
    ) {
      (value1.value as Record<string, string | number>)[answer.id + "_custom"] =
        customInputValue ?? 0;
    }
    if (answer.standalone != null ? !answer.standalone : replace) {
      promises.push(
        dispatch(
          calculatorActions.asyncReplaceAnswer({
            qId,
            qaId: answer.id,
            value: value1,
          })
        )
      );
      if (qId === "transport@4" && value === "no_details") {
        console.log("transport@4 no_details");
        [
          "transport@4_short_economy",
          "transport@4_short_business",
          "transport@4_medium_economy",
          "transport@4_medium_business",
          "transport@4_long_economy",
          "transport@4_long_business",
        ].forEach((id) => {
          dispatch(
            calculatorActions.removeData({
              questionId: id,
              answerId: "no_details",
            })
          );
        });
      }
      if (qId === "pet@1" && value === "no_details") {
        console.log("pet@1 no_details");
        dispatch(
          calculatorActions.removeData({
            questionId: "pet@1_cats",
            answerId: "no_details",
          })
        );
        dispatch(
          calculatorActions.removeData({
            questionId: "pet@1_weight_15",
            answerId: "no_details",
          })
        );
        dispatch(
          calculatorActions.removeData({
            questionId: "pet@1_weight_15_25",
            answerId: "no_details",
          })
        );
        dispatch(
          calculatorActions.removeData({
            questionId: "pet@1_weight_25",
            answerId: "no_details",
          })
        );
      }
    } else {
      promises.push(
        dispatch(
          calculatorActions.asyncUpdateAnswer({
            qId,
            qaId: answer.id,
            value: value1,
          })
        )
      );
    }
    await Promise.all(promises);
    setIsLoading(false);
    process.env.NODE_ENV === "development" && console.timeEnd("OnChange");
  };

  const deSelectAnswer = async (
    d: CalculatorAnswerTypes.BaseRadioData,
    optOrReplace?: CalculatorAnswerTypes.RadioOption | boolean,
    replaceAnswer?: boolean
  ) => {
    dispatch(
      calculatorActions.removeData({
        questionId: qId,
        answerId: answer.id,
      })
    );
    const replace =
      typeof optOrReplace === "boolean" ? optOrReplace : replaceAnswer;
    console.log("deSelectAnswer", d, optOrReplace, replaceAnswer, replace);
    if (answer.standalone != null ? !answer.standalone : replace) {
      await dispatch(
        calculatorActions.asyncReplaceAnswer({
          qId,
          qaId: answer.id,
          value: {
            value: { [answer.id]: "" },
            _meta: {
              [answer.id]: { ...d, category: state.currentQuestion!.category },
            },
          },
        })
      );
    } else {
      await dispatch(
        calculatorActions.asyncUpdateAnswer({
          qId,
          qaId: answer.id,
          value: {
            value: { [answer.id]: "" },
            _meta: {
              [answer.id]: { ...d, category: state.currentQuestion!.category },
            },
          },
        })
      );
    }
  };

  const isValue = (
    key: string,
    value: string | number,
    baseKey?: string
  ): boolean => {
    let baseIsValue =
      state.answers &&
      qId in state.answers &&
      typeof state.answers[qId] === "object" &&
      state.answers[qId].value != null &&
      (state.answers[qId].value as Record<string, string | number>)[
        answer.id
      ] === value;
    if (!baseIsValue) return false;
    if ("_meta" in state.answers[qId]) {
      const metaElement = state.answers[qId]._meta[answer.id];
      if (!metaElement) return baseIsValue;

      if (
        (metaElement.key === key || metaElement.key === baseKey) &&
        hasAnswerDataOptions(metaElement)
      ) {
        const optionHasKey = metaElement.options.some(
          (o) => o.key === key || o.key === value
        );
        return baseIsValue && optionHasKey;
      } else {
        return metaElement.key === key;
      }
    }
    return baseIsValue;
  };

  const anyHint = (
    (answer.data as CalculatorAnswerTypes.BaseRadioData[]) || []
  ).some((data) =>
    hasAnswerDataOptions(data) ? data.options.some((o) => o.hint) : !!data.hint
  );

  return (
    <>
      {((answer.data as CalculatorAnswerTypes.BaseRadioData[]) || []).map(
        (data) => {
          if (hasAnswerDataOptions(data)) {
            return (
              <div
                key={data.key}
                className={clsx(isLoading && styles.isLoading)}
              >
                <div className={clsx(data.type === "header" && styles.h)}>
                  {tPrisma.label(data) ?? data.key}
                  {data.hint && (
                    <span className={styles.hint}>{tPrisma.hint(data)}</span>
                  )}
                </div>
                <div
                  className={clsx(styles.content, anyHint && styles.hasHint)}
                >
                  {data.options.map((option) => {
                    const value = option.key;
                    return (
                      <RadioInput
                        checked={isValue(data.key, value)}
                        hint={option.hint ? tPrisma.hint(option) : undefined}
                        key={option.key}
                        label={tPrisma.label(option) ?? option.key}
                        onChange={(v) => onChange(v, data, option, true)}
                        qId={qId}
                        disabled={isLoading}
                        name={
                          answer.standalone ? qId + "_" + answer.id : undefined
                        }
                        value={value}
                        onClick={async (_e) => {
                          if (
                            option.deSelectable &&
                            (_e.target as HTMLElement).tagName !== "INPUT" &&
                            isValue(data.key, value)
                          ) {
                            await deSelectAnswer(data, option, true);
                            // await onChange("", data, option, true);
                          }
                        }}
                      >
                        {option.type === "input" && (
                          <Input
                            inputContainerClassName={styles.inputContainer}
                            inputProps={{
                              name: `${qId}_value`,
                              type: "number",
                              min: 0,
                              disabled: !isValue(option.key, value, data.key),
                              value: customInputValue,
                              onBlur: async (e) => {
                                const n = parseInt(e.target.value);
                                if (isNaN(n)) setCustomInputValue("");
                                else setCustomInputValue(n);
                                await onChange(value, data, option, false);
                              },
                              onChange: (e) => {
                                const n = parseInt(e.target.value);
                                if (isNaN(n)) setCustomInputValue("");
                                else setCustomInputValue(n);
                              },
                            }}
                          />
                        )}
                      </RadioInput>
                    );
                  })}
                </div>
              </div>
            );
          } else {
            let v = data.value ?? data.key;
            if (data._filter != null && data._filterBy != null) {
              const old = state.answers[data._filterBy].value;
              if (
                old != null &&
                Object.values(old).some((v) => v !== data._filter)
              ) {
                return null;
              }
            }
            let hint = data.hint ? tPrisma.hint(data) : undefined;
            let baseValue = v;
            if (
              qId === "energy@1" &&
              data.key !== "custom" &&
              data.key !== "no_details" &&
              !data.key.includes("_")
            ) {
              const h1Obj = Number(
                getValueFromAnswerValue(state.answers["home@2"].value)
              );
              baseValue = Math.round(
                Number(v) + 500 * (h1Obj / (h1Obj * h1Obj))
              );
              hint = `${baseValue} kWh`;
            }
            return (
              <RadioInput
                checked={isValue(data.key, baseValue)}
                hint={hint}
                key={data.key}
                label={tPrisma.label(data) ?? data.key}
                onChange={(v) => onChange(v, data, true)}
                qId={qId}
                disabled={isLoading}
                value={baseValue}
                className={clsx(
                  anyHint && styles.hasHint,
                  isLoading && styles.isLoading
                )}
              >
                {data.type === "input" && (
                  <Input
                    inputContainerClassName={styles.inputContainer}
                    inputProps={{
                      name: `${qId}_value`,
                      type: "number",
                      min: 0,
                      disabled: !isValue(data.key, v),
                      value: customInputValue,
                      onBlur: async (e) => {
                        const n = parseInt(e.target.value);
                        if (isNaN(n)) setCustomInputValue("");
                        else setCustomInputValue(n);
                        await onChange(v, data, false);
                      },
                      onChange: (e) => {
                        const n = parseInt(e.target.value);
                        if (isNaN(n)) setCustomInputValue("");
                        else setCustomInputValue(n);
                      },
                    }}
                  />
                )}
              </RadioInput>
            );
          }
        }
      )}
    </>
  );
}

function RadioInput<T extends string | number>({
  checked,
  children,
  className,
  disabled,
  hint,
  label,
  onChange,
  onClick,
  qId,
  value,
  name,
}: PropsWithChildren<
  {
    disabled?: boolean;
    qId: string;
    value: T;
    onChange: (value: T) => void;
    checked: boolean;
    label: string;
    hint?: string;
    name?: string;
    className?: string;
  } & Pick<LabelHTMLAttributes<HTMLLabelElement>, "onClick">
>) {
  return (
    <label className={clsx(styles.mo, className)} onClick={onClick}>
      <BasicRadioInput
        name={name ?? qId}
        type="radio"
        onChange={onChange}
        checked={checked}
        value={value}
        className={styles.radio}
        disabled={disabled}
      />
      <span className={styles.l}>{label}</span>
      {hint && <span className={styles.hint}>{hint}</span>}
      {children}
    </label>
  );
}

export default RadioAnswer;
